import axios from 'axios';
import { getRecaptchaToken, leadAction } from '@/lib/recaptcha';

// Same-origin fallback ('') when REACT_APP_BACKEND_URL isn't set at build
// time, so requests go to /api on whatever host served the app instead of
// the literal string "undefined/api".
export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL || ''}/api`,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Treat a non-JSON 2xx as a failure.
 *
 * Every lead form reports success on any 2xx. That is fine when /api reaches
 * the API, and server.cjs guarantees it does: it intercepts /api before the
 * static handler and answers 502 when the upstream is down, so axios throws
 * and the form shows its error.
 *
 * It is NOT guaranteed anywhere else. REACT_APP_BACKEND_URL is unset, so the
 * base URL falls back to same-origin /api, and any host with a catch-all SPA
 * rewrite — the default on most static hosts — answers POST /api/leads/contact
 * with 200 and index.html. axios sees 2xx, the form calls setResult, and the
 * visitor is told "Message received." while nothing was ever sent. That was
 * reproduced against the static preview server, which behaves exactly that
 * way.
 *
 * A lead silently disappearing is the worst failure this site has, so the
 * check lives here rather than in seven separate submit handlers: if the body
 * did not come back as JSON, it did not come from the API.
 *
 * No `response` is attached to the rejection, so each form falls through to
 * its own friendly copy rather than showing anything technical.
 */
api.interceptors.response.use(
  (res) => {
    const type = String(res?.headers?.['content-type'] || '');
    if (!type.includes('json')) {
      const err = new Error('API returned a non-JSON response; treating as a failed submission.');
      err.code = 'ERR_NON_JSON_RESPONSE';
      return Promise.reject(err);
    }
    return res;
  },
  (err) => Promise.reject(err)
);

/**
 * Bot protection for every lead submission.
 *
 * Each POST to /leads/* carries a fresh reCAPTCHA v3 token in a header, so
 * all forms are covered from this one place, including any added later. A
 * header rather than a body field because the lead models drop unknown
 * fields, and a token has no business being stored with the lead. When no
 * token can be obtained the request is still sent; the backend decides.
 */
api.interceptors.request.use(async (config) => {
  const action = String(config.method).toLowerCase() === 'post' ? leadAction(config.url) : null;
  if (!action) return config;
  const token = await getRecaptchaToken(action);
  if (token) config.headers['X-Recaptcha-Token'] = token;
  return config;
});

/**
 * Turn an axios error into something safe to render.
 *
 * FastAPI sends `detail` as a string for its own rejections and as an
 * array of {type, loc, msg, input, ctx} objects for Pydantic failures.
 * Rendering that array crashed React with error #31 and unmounted the
 * whole app, losing the visitor's answers, so this always returns a
 * string and never the raw value.
 */
export function errorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d.msg === "string");
    if (first) {
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : null;
      // "email: value is not a valid email address" reads better than the
      // bare message, and tells the visitor which field to go back to.
      return field ? `${String(field).replace(/_/g, " ")}: ${first.msg}` : first.msg;
    }
  }
  return fallback;
}
